import type { BuilderContent } from "@builder.io/sdk-react";

import {
  projectEntryRegion,
  regionBlocksOf,
} from "~/hec/builder/entry-regions";

/**
 * The two named `uiBlocks` regions on the `pdp-glp-1-pricing` model (ET-216).
 * One entry per medication can drive both halves of the treatment PDP, so a
 * marketer edits the price and the How-it-Works content in one place.
 *
 * `belowFold` does NOT displace `pdp-inline-content`: the PDP stacks both
 * below-fold sources (GLP-1 region first), so a product may carry a
 * `pdp-glp-1-pricing` entry and a `pdp-inline-content` entry at once — use
 * whichever model has the blocks you need.
 */
export const GLP1_PRICING_REGIONS = {
  /** In the buy column: the GLP1Pricing block + the Get Started CTA. */
  buyColumn: "buyColumn",
  /** Below the buy column: How it Works + FAQ. */
  belowFold: "belowFold",
} as const;

type Region = (typeof GLP1_PRICING_REGIONS)[keyof typeof GLP1_PRICING_REGIONS];

/**
 * The authored price. Only ever belongs in the buy column — a second copy below
 * the fold is never correct on this template, so it is stripped from the canvas
 * projection. See {@link resolveGlp1Regions}.
 */
const PRICE_COMPONENT_NAMES = ["GLP1Pricing"] as const;

/**
 * The authored intake CTA. NOT buy-column-only: a standalone Get Started below
 * the fold is legitimate (`TelehealthHowItWorks` carries one), so this is never
 * stripped — it only ever counts towards "this region is a buy column".
 */
const CTA_COMPONENT_NAMES = ["TelehealthFirstQuestion"] as const;

/**
 * The below-fold section the buy column's "How it Works" link points at. Named
 * explicitly so the link can be gated on that content actually being there —
 * see {@link hasGlp1HowItWorks}.
 */
const HOW_IT_WORKS_COMPONENT_NAMES = ["TelehealthHowItWorks"] as const;

const PRICE_COMPONENTS = new Set<string>(PRICE_COMPONENT_NAMES);
const CTA_COMPONENTS = new Set<string>(CTA_COMPONENT_NAMES);
const HOW_IT_WORKS_COMPONENTS = new Set<string>(HOW_IT_WORKS_COMPONENT_NAMES);

/**
 * Either half of the buy column. Presence of one of these is what makes a
 * region BE a buy column rather than just content that happens to sit there.
 *
 * This vocabulary is deliberately NARROW — the two purpose-built telehealth
 * blocks and nothing else (decided 2026-09-03). It is a pricing surface, so
 * there is no reason for a carousel or a core Button to be able to claim the
 * buy column. The consequence is real and accepted: an entry that composes its
 * price or CTA some other way — a core Button, a rich-text price, a shared
 * Symbol — is not recognised, so its content is treated as below-fold and the
 * coded price + CTA render instead. And a hand-authored button alongside a
 * `GLP1Pricing` yields TWO Get Started buttons, since the authored one isn't
 * counted (it would also skip the ET-12 availability gate and attribute
 * differently — see `TelehealthHowItWorks`, which is why an authorable link was
 * rejected there too).
 *
 * Broadening this is a product decision, not a bug fix: route it through
 * Product as a fast-follow rather than quietly adding names here.
 */
const BUY_COLUMN_COMPONENTS = new Set<string>([
  ...PRICE_COMPONENT_NAMES,
  ...CTA_COMPONENT_NAMES,
]);

const componentNameOf = (block: unknown): string =>
  (block as { component?: { name?: string } } | null)?.component?.name ?? "";

/**
 * Does this look like a Builder block rather than a plain option value? Used to
 * recognise a nested block array by its CONTENTS — see
 * {@link collectOptionBlockArrays}.
 */
const isBlockLike = (value: unknown): boolean =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  ("component" in value ||
    "@type" in value ||
    "children" in value ||
    "responsiveStyles" in value);

/**
 * Find nested block arrays anywhere inside a block's `component.options`.
 *
 * Enumerating container shapes does NOT work here, which a first cut of this
 * learned the hard way by handling only `Columns`. A registered component nests
 * children under whatever its `type: "uiBlocks"` input is CALLED, so the key is
 * per-component: this repo's own Accordion uses
 * `component.options.items[i].content`, Content-Tabs and Article-Grid use
 * `component.options.content`, and `pdp-glp-1-pricing` itself uses `buyColumn` /
 * `belowFold`. Any list of names goes stale the next time someone registers a
 * container — and the FAQ this module's docstring sends authors to build is most
 * likely an Accordion.
 *
 * So recognise the SHAPE instead: walk plain objects and arrays, and treat any
 * array whose members look like blocks as a nested block array. Recursion stops
 * at that boundary — the caller descends into the blocks themselves, so
 * continuing here would visit them twice.
 */
function collectOptionBlockArrays(value: unknown, out: unknown[][]): void {
  if (!value || typeof value !== "object") return;

  if (Array.isArray(value)) {
    if (value.some(isBlockLike)) {
      out.push(value);
      return;
    }
    for (const item of value) collectOptionBlockArrays(item, out);
    return;
  }

  for (const nested of Object.values(value as Record<string, unknown>)) {
    collectOptionBlockArrays(nested, out);
  }
}

/**
 * Every array a Builder block nests child blocks in.
 *
 * `children` covers Box/Section/Fragment, `blocks` covers the shapes carrying
 * children under that key, and {@link collectOptionBlockArrays} covers every
 * registered container's `uiBlocks` input (including `Columns`, whose
 * `component.options.columns[].blocks` needs no special case under a
 * shape-based scan).
 *
 * Symbol interiors are deliberately NOT traversed: a Symbol's blocks live in
 * another entry that isn't necessarily inlined here, and a shared Symbol is not
 * where a per-medication price gets copied.
 */
function childBlockArrays(block: unknown): unknown[][] {
  const b = block as Record<string, unknown> | null;
  if (!b) return [];

  const arrays: unknown[][] = [];
  if (Array.isArray(b.children)) arrays.push(b.children);
  if (Array.isArray(b.blocks)) arrays.push(b.blocks);

  collectOptionBlockArrays(
    (b.component as Record<string, unknown> | undefined)?.options,
    arrays,
  );

  return arrays;
}

/** Does `blocks` hold a block satisfying `match` at ANY depth? */
function containsBlock(
  blocks: unknown[],
  match: (block: unknown) => boolean,
): boolean {
  return blocks.some(block => {
    if (!block || typeof block !== "object") return false;
    if (match(block)) return true;
    return childBlockArrays(block).some(nested => containsBlock(nested, match));
  });
}

/** Match a block by its registered component name. */
const named =
  (names: Set<string>) =>
  (block: unknown): boolean =>
    names.has(componentNameOf(block));

const optionsOf = (block: unknown): Record<string, unknown> =>
  ((block as { component?: { options?: Record<string, unknown> } } | null)
    ?.component?.options ?? {}) as Record<string, unknown>;

/**
 * A `GLP1Pricing` block that will actually put a price on the page.
 *
 * Presence of the block isn't enough for the supersede decision. In MANUAL mode
 * (`useDefaultPrice: false`) the block renders from `regularPrice` /
 * `discountedPrice` and returns null when neither is set — so an author who
 * flips the toggle and publishes before filling the numbers in would otherwise
 * suppress the coded price too, and the PDP would show NO price at all despite
 * the catalog having one.
 *
 * Default mode always counts, blank catalog price included: there the block
 * reads the very price the coded column would, so the fallback has nothing
 * better to render.
 */
const canRenderPrice = (block: unknown): boolean => {
  const options = optionsOf(block);
  if (options.useDefaultPrice !== false) return true;
  return (
    typeof options.regularPrice === "number" ||
    typeof options.discountedPrice === "number"
  );
};

const containsComponent = (blocks: unknown[], names: Set<string>): boolean =>
  containsBlock(blocks, named(names));

const childBlockCount = (block: unknown): number =>
  childBlockArrays(block).reduce((total, arr) => total + arr.length, 0);

/**
 * Remove every block named in `names` at ANY depth, rebuilding containers
 * rather than mutating them.
 *
 * A container left EMPTY by the strip is dropped too — a Box that held only the
 * duplicated price would otherwise render as its own band of padding. Gated on
 * "had children before, has none after" so a deliberately childless block (a
 * styled spacer, a background-only Box) is untouched.
 */
function pruneComponents(blocks: unknown[], names: Set<string>): unknown[] {
  const kept: unknown[] = [];

  for (const block of blocks) {
    if (!block || typeof block !== "object") {
      kept.push(block);
      continue;
    }
    if (names.has(componentNameOf(block))) continue;

    const hadChildren = childBlockCount(block) > 0;
    const pruned = pruneWithin(block, names);
    if (hadChildren && childBlockCount(pruned) === 0) continue;

    kept.push(pruned);
  }

  return kept;
}

/** Apply {@link pruneComponents} to each of a block's child arrays. */
function pruneWithin(block: unknown, names: Set<string>): unknown {
  const b = block as Record<string, unknown>;
  const patch: Record<string, unknown> = {};

  if (Array.isArray(b.children))
    patch.children = pruneComponents(b.children, names);
  if (Array.isArray(b.blocks)) patch.blocks = pruneComponents(b.blocks, names);

  const component = b.component as Record<string, unknown> | undefined;
  if (component?.options) {
    const options = pruneOptionBlocks(component.options, names);
    if (options !== component.options)
      patch.component = { ...component, options };
  }

  return Object.keys(patch).length ? { ...b, ...patch } : block;
}

/**
 * The {@link collectOptionBlockArrays} traversal in rebuilding form: prune every
 * nested block array inside an options tree, copying only the branches that
 * actually changed so an untouched subtree keeps its identity. That identity is
 * load-bearing — it is how {@link pruneWithin} tells "nothing happened" from
 * "emptied", and it keeps the route's `useMemo` on the projection stable.
 */
function pruneOptionBlocks(value: unknown, names: Set<string>): unknown {
  if (!value || typeof value !== "object") return value;

  if (Array.isArray(value)) {
    if (value.some(isBlockLike)) {
      const pruned = pruneComponents(value, names);
      return pruned.length === value.length &&
        pruned.every((block, i) => block === value[i])
        ? value
        : pruned;
    }
    const mapped = value.map(item => pruneOptionBlocks(item, names));
    return mapped.every((item, i) => item === value[i]) ? value : mapped;
  }

  const source = value as Record<string, unknown>;
  const next: Record<string, unknown> = {};
  let changed = false;
  for (const [key, nested] of Object.entries(source)) {
    next[key] = pruneOptionBlocks(nested, names);
    if (next[key] !== nested) changed = true;
  }
  return changed ? next : value;
}

/**
 * Read one NAMED region. Legacy entries whose content sits in the default
 * canvas are handled by {@link resolveGlp1Regions}, not here — this only ever
 * reads the region it was asked for.
 */
export function selectPricingRegion(
  entry: BuilderContent | null | undefined,
  region: Region,
): BuilderContent | null {
  return projectEntryRegion(entry, region);
}

/** Project the entry's DEFAULT canvas (`data.blocks`) as its own section. */
export function selectDefaultBlocks(
  entry: BuilderContent | null | undefined,
): BuilderContent | null {
  return projectEntryRegion(entry, "blocks");
}

/** Which region a below-fold section came from. Stable across products. */
export type Glp1BelowFoldSource = "belowFold" | "canvas";

export interface Glp1BelowFoldSection {
  /**
   * Which region this came from, used by the route to build a React key.
   * Region-derived rather than positional because the SAME index means
   * different content on different entries — index 0 is the `belowFold` field
   * on a legacy entry but the canvas on a migrated one — so an index key would
   * let React reuse one `<Content>` instance across two structurally different
   * trees.
   *
   * NB the region alone is not a sufficient key across PRODUCTS: navigating
   * between two migrated treatment PDPs yields `canvas` on both. The route
   * therefore combines this with the entry id; see its key construction.
   */
  source: Glp1BelowFoldSource;
  content: BuilderContent;
}

const isSection = (s: Glp1BelowFoldSection | null): s is Glp1BelowFoldSection =>
  s !== null;

export interface Glp1RegionLayout {
  /** Buy-column content (price + CTA), or null to keep the coded pair. */
  buyColumn: BuilderContent | null;
  /** Below-the-fold sections from this entry, in render order. */
  belowFold: Glp1BelowFoldSection[];
}

/**
 * Resolve one `pdp-glp-1-pricing` entry into its buy-column and below-fold
 * sections. Two layouts, chosen PER ENTRY by whether `buyColumn` is authored:
 *
 * **Main-canvas-below-fold** (`buyColumn` populated) — the buy column reads the
 * `buyColumn` field, which frees the entry's default canvas for below-fold
 * content. That matters for authoring, not aesthetics: Builder's nested
 * blocks-field insert menu offers only registered custom components, while the
 * main canvas offers Symbols and built-in blocks too. The buy column needs just
 * `GLP1Pricing` + `TelehealthFirstQuestion` (both custom components, both
 * available in the restricted palette); the below-fold half is where authors
 * need a Symbol — e.g. one shared FAQ referenced by all 8 medication PDPs
 * instead of 8 copies. So each half gets the region whose palette it needs.
 *
 * **Legacy** (`buyColumn` empty) — the default canvas IS the buy column, which
 * is how every entry authored before the `buyColumn` field behaved. Keeping
 * this branch is what makes the change safe to deploy independently of content:
 * a published entry doesn't move its price below the fold the moment this ships,
 * and an author opts in per medication simply by filling `buyColumn` in.
 *
 * The migration hazard that branch creates is an author who COPIES the price
 * blocks into `buyColumn` instead of moving them — nothing in the editor says
 * the canvas changed meaning. That would render the price a second time below
 * the fold, which is never correct on this template, so
 * {@link PRICE_COMPONENTS} is stripped from the canvas projection whenever the
 * migrated layout is active. The strip recurses: the docstring sends authors to
 * the canvas precisely because that palette has Box / Columns / Section, so the
 * copied price usually lands INSIDE a container rather than at the top level.
 * A duplicated CTA still renders (a standalone Get Started below the fold is
 * legitimate, so it can't be filtered blind) — but it attributes as
 * `first-question`, not `pdp-main`, because the buy column's
 * `TelehealthPdpPriceProvider` doesn't wrap the below-fold slot.
 *
 * The legacy branch has a mirror-image hazard, and it is structural rather than
 * documented: the natural authoring path on the canvas — drop a shared FAQ
 * Symbol, never touch `buyColumn` — would put the FAQ *in* the buy column,
 * replacing the price and CTA with nothing below the fold. So the canvas is
 * adopted as the buy column only when it actually CONTAINS one
 * ({@link BUY_COLUMN_COMPONENTS}); otherwise it is treated as below-fold
 * content and the coded price + CTA pair stays. "Canvas holds the price" is
 * what the legacy entries this branch exists for look like, so they are
 * unaffected.
 *
 * `belowFold` (the named field) renders BEFORE the main canvas — it holds the
 * How-it-Works section the buy column's anchor link targets, and the design puts
 * that above the FAQ.
 */
export function resolveGlp1Regions(
  entry: BuilderContent | null | undefined,
): Glp1RegionLayout {
  const buyColumnField = selectPricingRegion(
    entry,
    GLP1_PRICING_REGIONS.buyColumn,
  );
  const belowFoldField = selectPricingRegion(
    entry,
    GLP1_PRICING_REGIONS.belowFold,
  );

  const section = (
    source: Glp1BelowFoldSource,
    content: BuilderContent | null,
  ): Glp1BelowFoldSection | null => (content ? { source, content } : null);

  if (buyColumnField) {
    const canvas = projectEntryRegion(entry, "blocks", blocks =>
      pruneComponents(blocks, PRICE_COMPONENTS),
    );
    return {
      buyColumn: buyColumnField,
      belowFold: [
        section("belowFold", belowFoldField),
        section("canvas", canvas),
      ].filter(isSection),
    };
  }

  const canvas = selectDefaultBlocks(entry);
  const canvasIsBuyColumn = containsComponent(
    regionBlocksOf(canvas),
    BUY_COLUMN_COMPONENTS,
  );

  return {
    buyColumn: canvasIsBuyColumn ? canvas : null,
    belowFold: [
      section("belowFold", belowFoldField),
      canvasIsBuyColumn ? null : section("canvas", canvas),
    ].filter(isSection),
  };
}

/** What an authored buy column actually contributes. See {@link describeGlp1BuyColumn}. */
export interface Glp1BuyColumnContents {
  /** An authored price block is present. */
  price: boolean;
  /** An authored intake CTA is present. */
  cta: boolean;
}

/**
 * Which halves of the buy column an authored region supplies, so the caller can
 * keep the coded counterpart for the half it does NOT.
 *
 * Presence-of-region is not a sufficient signal on its own: the route wraps the
 * projection in context providers before handing it over, so the slot is truthy
 * whenever an entry resolves — including for an entry where the author added
 * `GLP1Pricing` but no CTA. Superseding on that would leave a live Rx PDP with
 * no path forward at all, since the treatment template has no Add-to-Cart
 * fallback by design.
 *
 * Structural (does the region contain the block?) rather than a render-time
 * signal, because the decision has to be made before the Builder subtree
 * renders. Where the entry data is enough to prove the block CANNOT render a
 * price, that is honoured — see {@link canRenderPrice} for the manual-mode case.
 * What remains is default mode against a product with no catalog price: that
 * still counts as "authored", because it is the same blank the coded
 * column would render from the same missing catalog price, so the fallback has
 * nothing better to offer; the CTA, which is the floor that matters, is
 * unconditional inside `TelehealthFirstQuestion`.
 */
export function describeGlp1BuyColumn(
  content: BuilderContent | null | undefined,
): Glp1BuyColumnContents {
  const blocks = regionBlocksOf(content ?? null);
  return {
    price: containsBlock(
      blocks,
      block => named(PRICE_COMPONENTS)(block) && canRenderPrice(block),
    ),
    cta: containsComponent(blocks, CTA_COMPONENTS),
  };
}

/**
 * Does this entry's below-fold content include the section the buy column's
 * "How it Works" link targets?
 *
 * Gating that link on "any below-fold section exists" is not enough, in two
 * ways that both produce a link labelled for content it does not lead to: the
 * route's stacked list also holds a `pdp-inline-content` entry, and — since the
 * canvas guardrail — this entry's own demoted canvas, which is typically the
 * shared FAQ Symbol. So ask for the block by name.
 *
 * The trade-off is deliberate: an author who hand-builds a "how it works" out
 * of rich text instead of the block gets no link. Better a missing link than
 * one that lies, and `TelehealthHowItWorks` exists precisely for this section.
 */
export function hasGlp1HowItWorks(
  sections: readonly Glp1BelowFoldSection[],
): boolean {
  return sections.some(({ content }) =>
    containsComponent(regionBlocksOf(content), HOW_IT_WORKS_COMPONENTS),
  );
}
